import type {
  CampaignAnalyticsGranularity,
  CampaignAnalyticsKpi,
  CampaignAnalyticsTrendPoint,
} from "@adpilot/shared";
import type { TFunction } from "i18next";

export type PerformanceMetric = CampaignAnalyticsKpi["key"];

export type PerformanceKpi<M extends string = PerformanceMetric> = Omit<
  CampaignAnalyticsKpi,
  "key"
> & {
  key: M;
  /** undefined preserves legacy formatting; null explicitly means unknown currency. */
  currency?: string | null;
  changeKind?: "relative" | "percentage-points";
  description?: string;
  loading?: boolean;
};

export type PerformanceTrendPoint<M extends string = PerformanceMetric> = Pick<
  CampaignAnalyticsTrendPoint,
  "periodStart" | "periodEnd" | "dataStatus"
> &
  Partial<Record<M, number | null>> & {
    metricDataStatus?: Partial<Record<M, CampaignAnalyticsTrendPoint["dataStatus"]>>;
  };

/** Every metric the campaign-analytics read model exposes, in menu order. */
export const PERFORMANCE_METRICS: readonly PerformanceMetric[] = [
  "spend",
  "sales",
  "orders",
  "acos",
  "cpc",
  "cpa",
  "roas",
  "clicks",
  "impressions",
  "ctr",
  "cvr",
] as const;

/** Default six-card row; each card picks its own metric from the menu. */
export const DEFAULT_PERFORMANCE_SLOTS: readonly PerformanceMetric[] = [
  "spend",
  "sales",
  "orders",
  "acos",
  "cpc",
  "cpa",
] as const;

export const PERFORMANCE_GRANULARITIES: readonly CampaignAnalyticsGranularity[] = [
  "day",
  "week",
  "month",
] as const;

export function kpiTone(kpi: PerformanceKpi<string>): "success" | "destructive" | "muted" {
  if (kpi.change == null || kpi.direction === "flat") return "muted";
  // Volume metrics have no good or bad direction; colouring them would imply
  // a judgement the data does not support.
  if (kpi.semantics === "neutral") return "muted";
  const good =
    (kpi.semantics === "positive-is-good" && kpi.direction === "up") ||
    (kpi.semantics === "positive-is-bad" && kpi.direction === "down");
  return good ? "success" : "destructive";
}

export function kpiChangeLabel(kpi: PerformanceKpi<string>, t: TFunction<"campaign">): string {
  if (kpi.previousValue == null) return t("performance.kpiChange.previousUnavailable");
  if (kpi.changeKind === "percentage-points") {
    if (kpi.value == null) return t("performance.kpiChange.comparisonUnavailable");
    const difference = (kpi.value - kpi.previousValue) * 100;
    if (difference === 0) return t("performance.kpiChange.noChange");
    return t("performance.kpiChange.percentagePoints", {
      change: `${difference > 0 ? "+" : "−"}${Math.abs(difference).toFixed(1)}`,
    });
  }
  if (kpi.previousValue === 0) return t("performance.kpiChange.previousZero");
  if (kpi.change == null) return t("performance.kpiChange.comparisonUnavailable");
  if (kpi.direction === "flat") return t("performance.kpiChange.noChange");
  const sign = kpi.direction === "up" ? "+" : "−";
  const change = `${sign}${Math.abs(kpi.change * 100).toFixed(1)}%`;
  return t("performance.kpiChange.vsPrevious", { change });
}

export function formatTrendPeriod(periodStart: string, periodEnd: string, locale: string): string {
  const start = new Date(`${periodStart}T00:00:00.000Z`);
  const end = new Date(`${periodEnd}T00:00:00.000Z`);
  const format = new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
  const startLabel = format.format(start);
  return periodStart === periodEnd ? startLabel : `${startLabel}–${format.format(end)}`;
}

/** Events marked on the trend, e.g. the 操作日志 changes in the same window. */
export interface PerformanceTrendMarkers {
  /** Tooltip / data-table name for the count. */
  label: string;
  /** Events per local day (YYYY-MM-DD); each bucket sums the days it spans. */
  dailyCounts: readonly { date: string; count: number }[];
}

export function countMarkersInPeriod(
  dailyCounts: PerformanceTrendMarkers["dailyCounts"],
  periodStart: string,
  periodEnd: string,
): number {
  let total = 0;
  for (const day of dailyCounts) {
    if (day.date >= periodStart && day.date <= periodEnd) total += day.count;
  }
  return total;
}

export function hasIncompleteTrendData(
  trend: Pick<CampaignAnalyticsTrendPoint, "periodStart" | "dataStatus">[],
  dataThrough: string | null,
): boolean {
  if (!dataThrough) return false;
  return trend.some((point) => point.periodStart <= dataThrough && point.dataStatus !== "complete");
}

/**
 * Reads persisted slot metrics defensively: the stored value predates any future
 * metric-set change and is user-editable, so anything malformed, duplicated, off
 * the panel's metric list, or the wrong length falls back to the default row
 * rather than rendering a broken card grid.
 */
export function parsePerformanceSlots<M extends string>(
  raw: string | null,
  metrics: readonly M[],
  defaultSlots: readonly M[],
): M[] {
  const fallback = [...defaultSlots];
  if (!raw) return fallback;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return fallback;
  }
  if (!Array.isArray(parsed) || parsed.length !== defaultSlots.length) return fallback;
  if (!parsed.every((value) => (metrics as readonly string[]).includes(value as string))) {
    return fallback;
  }
  if (new Set(parsed).size !== parsed.length) return fallback;
  return parsed as M[];
}

/**
 * A metric already on the row is rejected rather than moved: the menu disables
 * those entries, so this only guards against a caller bypassing that state. The
 * row can therefore never render the same metric twice.
 */
export function setPerformanceSlot<M extends string>(
  slots: readonly M[],
  index: number,
  metric: M,
): M[] {
  const current = slots[index];
  if (current == null || current === metric) return [...slots];
  if (slots.includes(metric)) return [...slots];
  return slots.map((slot, slotIndex) => (slotIndex === index ? metric : slot));
}

/** Keeps the charted metric addressable when the URL points outside the row. */
export function resolveSelectedMetric<M extends string>(slots: readonly M[], metric: M): M {
  return slots.includes(metric) ? metric : (slots[0] ?? metric);
}
